import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Card, CardContent, LinearProgress, Stack, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveShow } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import RingStateSection from "../components/RingStateSection";
import ScribePresenceSection from "../components/ScribePresenceSection";
import useRingStates from "../hooks/useRingStates";

// #823 — the dashboard was a one-shot fetch (only re-ran when activeShowId
// changed), so anyone who left it open during a live show read stale counts
// forever. Poll and refresh-on-focus, same visibility-aware shape as
// announcer-fe's useClassResults.js / common-fe's useRideStatuses.js: pause
// while the tab is hidden, refresh immediately the moment it's visible again
// so nothing is stale on return. 20s (vs. announcer's 5-15s) because this
// aggregate re-walks every class in the show (2 calls each), not one class's
// results.
const DASHBOARD_POLL_MS = 20000;

// #511: replace the placeholder with a real read-only counts/completion
// dashboard for the selected show. Shape (useScoreApi/unwrap, TableSkeleton
// loading region) originally copied from ClassManager.jsx / PeopleManager.jsx;
// #823 swapped the one-shot fetch those pages use for the visibility-aware
// poll below, since a dashboard (unlike a CRUD manager page) needs to stay
// current while left open.
//
// Counts are derived FROM the show's own classes/rides/results (not org-wide
// listPeople/listHorses) so they reflect this show only:
//   classes = listClasses(showId)
//   for each class: rides = listRides(classId), results = getClassResults(classId)
//   classCount   = classes.length
//   rideCount    = sum of rides.length
//   riderCount   = distinct ride.riderId across all classes
//   horseCount   = distinct ride.horseId across all classes
//   finalCount   = sum of results where status === "FINAL"
// (results.js:buildRideResult returns exactly one result per ride, so
// finalCount/rideCount is directly comparable.)
//
// The aggregate loop (classes -> Promise.all(rides, results) per class) can
// take a moment for a big show, so it has its own loading flag distinct from
// the show-list load, and renders a stat-card skeleton rather than a blank
// flash (MD3 loading convention, #514).
async function loadShowAggregate(scoreApi, showId) {
  const classes = unwrap(await scoreApi.classes.listClasses(showId));

  const perClass = await Promise.all(
    classes.map(async (c) => {
      const [rides, results] = await Promise.all([
        unwrap(await scoreApi.rides.listRides(c.id)),
        unwrap(await scoreApi.results.getClassResults(c.id)),
      ]);
      return { rides, results };
    })
  );

  const riderIds = new Set();
  const horseIds = new Set();
  let rideCount = 0;
  let finalCount = 0;

  for (const { rides, results } of perClass) {
    rideCount += rides.length;
    for (const ride of rides) {
      if (ride.riderId) riderIds.add(ride.riderId);
      if (ride.horseId) horseIds.add(ride.horseId);
    }
    finalCount += results.filter((r) => r.status === "FINAL").length;
  }

  return {
    classCount: classes.length,
    rideCount,
    riderCount: riderIds.size,
    horseCount: horseIds.size,
    finalCount,
  };
}

// #1061 — the on-page heading (NOT the sidebar nav label, NOT the browser
// tab title — those stay "Dashboard") shows the active show's name instead
// of the generic "Show dashboard". The show's year is appended after the
// name, but only when the name doesn't already contain one (a show is
// commonly named "Foo Classic 2026", and doubling the year there would be
// redundant/wrong) — checked with a real-year-shaped token match anywhere in
// the string, not just at the end, so "2026 Foo Classic" also counts.
const YEAR_TOKEN_RE = /\b(19|20)\d{2}\b/;

// activeShow.startDate is a bare YYYY-MM-DD (see Settings.jsx's
// formatShowDate) — split it rather than routing through `new Date(iso)`,
// which treats a date-only string as UTC midnight and can roll the year
// back a day off in far-eastern/western clocks.
function showYearFrom(startDate) {
  if (!startDate) return null;
  const [y] = startDate.split("-");
  const year = Number(y);
  return Number.isInteger(year) && year > 0 ? String(year) : null;
}

function dashboardHeading(t, activeShow) {
  const name = activeShow?.name;
  if (!name) return t("dashboard.title");
  const year = showYearFrom(activeShow?.startDate);
  if (!year || YEAR_TOKEN_RE.test(name)) return t("dashboard.heading", { name });
  return t("dashboard.headingWithYear", { name, year });
}

// #1060 — the org's uploaded logo (score-api's `GET /show` family already
// returns a presigned `logoUrl` off the stored `logoKey`, per #1058) renders
// right-aligned next to the #1061 heading, with the org name (the same
// `organization` field the report footer / Settings.jsx's LogoCard read)
// underneath it. Bounded size — large but not full-bleed — via maxHeight/
// maxWidth + objectFit: "contain", same convention as Settings.jsx's
// show-logo-preview. No logo: fall back to the org name alone (cleaner than
// an empty box); no org name either: render nothing rather than an artifact.
function ShowLogoBlock({ activeShow, t }) {
  const logoUrl = activeShow?.logoUrl;
  const organization = activeShow?.organization;

  if (!logoUrl && !organization) return null;

  return (
    <Stack alignItems="flex-end" spacing={0.5} data-testid="dashboard-logo-block">
      {logoUrl && (
        <Box
          component="img"
          src={logoUrl}
          alt={t("dashboard.logoAlt", { organization: organization || "" })}
          data-testid="dashboard-logo"
          sx={{
            maxHeight: { xs: 64, sm: 100 },
            maxWidth: { xs: 160, sm: 240 },
            objectFit: "contain",
          }}
        />
      )}
      {organization && (
        <Typography variant="body2" color="text.primary" data-testid="dashboard-org-name">
          {organization}
        </Typography>
      )}
    </Stack>
  );
}

function StatCard({ label, value }) {
  return (
    <Card variant="outlined" sx={{ minWidth: 140, flex: "1 1 140px" }}>
      <CardContent>
        <Typography variant="h4" color="text.primary">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function ShowDashboard() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.dashboard"));
  const scoreApi = useScoreApi();
  const { activeShowId, activeShow, loading: loadingShows } = useActiveShow();

  // Single fetch for RingStateSection AND ScribePresenceSection (#2365): both
  // need the same per-ring class/ride/ring-state data, and calling
  // useRingStates a second time here duplicated every underlying request
  // (listClasses/listRides/listRingStates) on every dashboard mount -- real
  // extra load, and enough added concurrent traffic during mount to push the
  // #823 "pauses polling while hidden" test's timing past its margin. One
  // fetch, passed down as a prop to both.
  const ringState = useRingStates(activeShowId);

  const [error, setError] = useState(null);

  const [agg, setAgg] = useState(null);
  const [aggLoading, setAggLoading] = useState(true);

  const scoreApiRef = useRef(scoreApi);
  scoreApiRef.current = scoreApi;
  const showIdRef = useRef(activeShowId);
  showIdRef.current = activeShowId;

  // Only the FIRST load for a given show shows the skeleton — a background
  // poll refresh swaps the numbers in place, it doesn't blank the screen.
  const hasLoadedForShowRef = useRef(false);
  useEffect(() => {
    hasLoadedForShowRef.current = false;
  }, [activeShowId]);

  const refresh = useCallback(async () => {
    const showId = showIdRef.current;
    if (!showId) return;
    try {
      if (!hasLoadedForShowRef.current) setAggLoading(true);
      const result = await loadShowAggregate(scoreApiRef.current, showId);
      setAgg(result);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      hasLoadedForShowRef.current = true;
      setAggLoading(false);
    }
  }, []);

  // (Re)load the aggregate for the active show, then keep it current: poll at
  // DASHBOARD_POLL_MS, pausing while the tab is hidden and refreshing
  // immediately on return, so a scoreadmin who leaves this open during a
  // show sees live counts rather than whatever was true when the tab loaded.
  useEffect(() => {
    if (!activeShowId) {
      setAgg(null);
      setAggLoading(false);
      return undefined;
    }

    if (document.visibilityState !== "hidden") refresh();

    let timer = null;
    const start = () => {
      if (document.visibilityState === "hidden") return;
      if (timer == null) timer = setInterval(refresh, DASHBOARD_POLL_MS);
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") stop();
      else {
        refresh();
        start();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [activeShowId, refresh]);

  // MD3 loading convention (#514): the initial show-list fetch is a full
  // content region, so it renders a shaped skeleton (not a blank flash).
  if (loadingShows) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h4" gutterBottom color="text.primary" data-testid="dashboard-heading">
            {dashboardHeading(t, activeShow)}
          </Typography>
          <ShowLogoBlock activeShow={activeShow} t={t} />
        </Stack>
        <TableSkeleton rows={1} columns={4} />
      </Box>
    );
  }

  const completionPct = agg && agg.rideCount > 0 ? (agg.finalCount / agg.rideCount) * 100 : 0;

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h4" gutterBottom color="text.primary" data-testid="dashboard-heading">
          {dashboardHeading(t, activeShow)}
        </Typography>
        <ShowLogoBlock activeShow={activeShow} t={t} />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("dashboard.loadError")}
        </Alert>
      )}

      {!activeShowId && <Typography color="text.secondary">{t("dashboard.noShows")}</Typography>}

      {activeShowId && aggLoading && <TableSkeleton rows={1} columns={4} />}

      {activeShowId && !aggLoading && agg && (
        <Stack spacing={3} data-testid="dashboard-stats">
          <Stack direction="row" flexWrap="wrap" gap={2}>
            <StatCard label={t("dashboard.stats.classes")} value={agg.classCount} />
            <StatCard label={t("dashboard.stats.rides")} value={agg.rideCount} />
            <StatCard label={t("dashboard.stats.riders")} value={agg.riderCount} />
            <StatCard label={t("dashboard.stats.horses")} value={agg.horseCount} />
          </Stack>

          <Card variant="outlined" data-testid="dashboard-completion">
            <CardContent>
              <Typography variant="h6" gutterBottom color="text.primary">
                {t("dashboard.completion.title")}
              </Typography>
              <Typography variant="body1" color="text.primary" gutterBottom>
                {t("dashboard.completion.summary", {
                  final: agg.finalCount,
                  total: agg.rideCount,
                })}
              </Typography>
              <LinearProgress variant="determinate" value={completionPct} />
            </CardContent>
          </Card>

          {/* #1936 (docs/score/ring-state.md) — the Ring section: every ring
              in the show, what's in it, where that came from, and one-click
              controls to fix it. This is the live-monitoring screen the
              card's "screen you open mid-class" framing points at. */}
          <RingStateSection showId={activeShowId} ringState={ringState} />

          {/* #2365 (scribe-presence workstream, card D) — who is connected
              at which judge seat, by ring, next to the expected staffing. */}
          <ScribePresenceSection showId={activeShowId} ringState={ringState} />
        </Stack>
      )}
    </Box>
  );
}
